import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import authTokenManager from '../utils/authToken.js';
import rbac from '../utils/roleBasedAccess.js';
import secureApiClient from '../utils/secureApiClient.js';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsProfileSetup, setNeedsProfileSetup] = useState(false);

  // Roles are always issued by the server. Missing/legacy profiles must never
  // receive a staff role based on a display name in the browser.
  const determineUserRole = (userData) => {
    return userData?.role || 'client';
  };

  // Check if user needs profile setup
  const checkProfileSetup = (userData) => {
    // User needs profile setup if they don't have firstName, lastName, or role
    const needsSetup = !userData?.firstName || !userData?.lastName || !userData?.role;
    return needsSetup;
  };

  // Initialize user with token management
  const initializeUserWithTokens = async (user, userData) => {
    try {
      const userName = userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
      const userRole = determineUserRole(userData);
      
      const userProfile = {
        uid: user.uid,
        email: user.email,
        ...userData,
        // emailVerified must come AFTER ...userData so the live Firebase Auth
        // value wins over the potentially stale value stored in Firestore.
        emailVerified: user.emailVerified,
        name: userName,
        role: userRole
      };

      // Check if user needs profile setup
      if (checkProfileSetup(userData)) {
        setNeedsProfileSetup(true);
        setCurrentUser(userProfile);
        return userProfile;
      }

      // Set user in RBAC system
      rbac.setUser(userProfile);
      
      // Generate access token
      await authTokenManager.generateAccessToken();
      
      setCurrentUser(userProfile);
      setNeedsProfileSetup(false);
      
      return userProfile;
    } catch (error) {
      throw error;
    }
  };

  // Manual refresh function to force update current user
  const refreshCurrentUser = async () => {
    const user = auth.currentUser;
    if (user) {
      await user.reload();
      const freshUser = auth.currentUser;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', freshUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          await initializeUserWithTokens(freshUser, userData);
        } else {
          const fallbackUser = {
            uid: freshUser.uid,
            email: freshUser.email,
            emailVerified: freshUser.emailVerified,
            name: freshUser.displayName || 'User',
            role: 'client'
          };
          setCurrentUser(fallbackUser);
          setNeedsProfileSetup(true);
        }
      } catch (error) {
      }
    }
  };

  async function signup({ email, password, firstName, lastName, invitationToken, hipaaAcknowledgedAt }) {
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Roles are assigned only by the server. A missing invitation creates a
      // client profile; a staff invitation is validated and consumed server-side.
      await secureApiClient.createUserProfile(firstName, lastName, {
        ...(invitationToken && { invitationToken }),
        hipaaAcknowledged: Boolean(hipaaAcknowledgedAt)
      });

      await sendEmailVerification(user);

      return true;
    } catch (error) {
      throw error;
    }
  }

  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Reload user to get the latest email verification status
      await user.reload();
      
      // Get the fresh user object after reload
      const freshUser = auth.currentUser;
      
      // Check if email is verified. Deliberately NOT signing the user out here —
      // ProtectedRoute independently blocks unverified users from every protected
      // route, and keeping the session alive means auth.currentUser stays populated
      // so "Resend verification email" (on this error and on /verify-email) can
      // actually send — otherwise there's no signed-in user for Firebase to send to.
      if (!freshUser.emailVerified) {
        throw new Error('Please verify your email before logging in.');
      }

      // Get user data from Firestore and initialize with tokens
      const userDoc = await getDoc(doc(db, 'users', freshUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        await initializeUserWithTokens(freshUser, userData);
      } else {
        // Fallback for users without Firestore document
        const fallbackUser = {
          uid: freshUser.uid,
          email: freshUser.email,
          emailVerified: freshUser.emailVerified,
          name: freshUser.displayName || 'User',
          role: 'client'
        };
        await initializeUserWithTokens(freshUser, fallbackUser);
      }

      return true;
    } catch (error) {
      throw error;
    }
  }

  async function logout() {
    try {
      // Clear tokens before signing out
      authTokenManager.clearTokens();
      
      await signOut(auth);
      setCurrentUser(null);
      
      // Clear RBAC user
      rbac.setUser(null);
      
      return true;
    } catch (error) {
      throw error;
    }
  }

  async function acknowledgeConsent() {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');
    await secureApiClient.acknowledgeAgreement('consent');
    // Update local currentUser state so the gate disappears immediately
    setCurrentUser(prev => ({ ...prev, consentAcknowledgedAt: new Date().toISOString() }));
  }

  async function acknowledgeHIPAA() {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');
    await secureApiClient.acknowledgeAgreement('hipaa');
    // Update local currentUser state so the gate disappears immediately
    setCurrentUser(prev => ({ ...prev, hipaaAcknowledgedAt: new Date().toISOString() }));
  }

  async function forgotPassword(email) {
    await sendPasswordResetEmail(auth, email);
  }

  async function resendVerificationEmail() {
    try {
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        return true;
      }
      return false;
    } catch (error) {
      throw error;
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Reload user to get the latest email verification status
          await user.reload();
          const freshUser = auth.currentUser;
          
          // Get user data from Firestore
          const userDoc = await getDoc(doc(db, 'users', freshUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            await initializeUserWithTokens(freshUser, userData);
          } else {
            // Fallback if no Firestore document exists
            const fallbackUserName = freshUser.displayName || 'User';
            const fallbackUser = {
              uid: freshUser.uid,
              email: freshUser.email,
              emailVerified: freshUser.emailVerified,
              name: fallbackUserName,
              role: determineUserRole(null)
            };
            await initializeUserWithTokens(freshUser, fallbackUser);
          }
        } catch (error) {
          const errorFallbackUserName = user.displayName || 'User';
          const errorFallbackUser = {
            uid: user.uid,
            email: user.email,
            emailVerified: user.emailVerified,
            name: errorFallbackUserName,
            role: determineUserRole(null)
          };
          setCurrentUser(errorFallbackUser);
        }
      } else {
        setCurrentUser(null);
        // Clear tokens and RBAC when no user
        authTokenManager.clearTokens();
        rbac.setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    logout,
    acknowledgeConsent,
    acknowledgeHIPAA,
    resendVerificationEmail,
    forgotPassword,
    refreshCurrentUser,
    loading,
    needsProfileSetup,
    setNeedsProfileSetup,
    // Add token management functions
    getValidAccessToken: authTokenManager.getValidAccessToken,
    refreshAccessToken: authTokenManager.refreshAccessToken,
    clearTokens: authTokenManager.clearTokens,
    isAuthenticated: authTokenManager.isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
